import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';

import { useFixedRoutes } from '../../context/FixedRoutesContext';
import { useNetworkBrowse } from '../../context/NetworkBrowseContext';
import { useRouteOverlap } from '../../context/RouteOverlapContext';
import type { FixedRoute } from '../../types/fixedRoutes';
import type { CreateRouteOverlapRunRequest } from '../../services/routeOverlap/types';

function buildCandidateGeometry(route: FixedRoute, stopLookup: Map<string, { lat: number; lng: number }>): [number, number][] {
  if (route.geometry?.coordinates && route.geometry.coordinates.length >= 2) {
    return route.geometry.coordinates;
  }
  return route.stops
    .map((stop) => stopLookup.get(stop.stopId))
    .filter((stop): stop is { lat: number; lng: number } => !!stop)
    .map((stop) => [stop.lat, stop.lng]);
}

function routeLabel(route: FixedRoute): string {
  return route.shortName ? `${route.shortName} - ${route.name}` : route.name;
}

export default function RouteOverlapAnalysisModal(): React.ReactElement {
  const { routes, stops } = useFixedRoutes();
  const { feedId, version, scenarioId, setPickerOpen } = useNetworkBrowse();
  const {
    isModalOpen,
    closeModal,
    selectedCandidateRouteId,
    setSelectedCandidateRouteId,
    run,
    status,
    isStarting,
    error,
    toleranceMetersDefault,
    selectedComparisonRouteId,
    focusComparisonRoute,
    startRun,
    cancelRun,
    clearError,
  } = useRouteOverlap();
  const [toleranceInput, setToleranceInput] = useState(String(toleranceMetersDefault));

  const stopLookup = useMemo(
    () => new Map(stops.map((stop) => [stop.id, stop.coordinates] as const)),
    [stops],
  );

  const selectedRoute = useMemo(
    () => routes.find((route) => route.id === selectedCandidateRouteId) ?? null,
    [routes, selectedCandidateRouteId],
  );

  const candidateGeometry = useMemo(
    () => (selectedRoute ? buildCandidateGeometry(selectedRoute, stopLookup) : []),
    [selectedRoute, stopLookup],
  );

  useEffect(() => {
    setToleranceInput(String(run?.config.toleranceMeters ?? toleranceMetersDefault));
  }, [run?.config.toleranceMeters, toleranceMetersDefault]);

  const handleRun = async () => {
    if (!selectedRoute || !feedId) {
      return;
    }
    const parsedTolerance = Number(toleranceInput);
    const payload: CreateRouteOverlapRunRequest = {
      version: scenarioId ?? version ?? undefined,
      candidate: {
        routeId: selectedRoute.id,
        routeName: selectedRoute.name,
        geometryCoordinates: candidateGeometry,
        stopIds: selectedRoute.stops.map((stop) => stop.stopId),
        startTime: selectedRoute.startTime,
        endTime: selectedRoute.endTime,
      },
      toleranceMeters: Number.isFinite(parsedTolerance) ? parsedTolerance : undefined,
    };
    const created = await startRun(payload);
    if (created?.comparisons?.[0]) {
      focusComparisonRoute(created.comparisons[0].routeId);
    }
  };

  const selectedComparison = run?.comparisons.find((comparison) => comparison.routeId === selectedComparisonRouteId) ?? null;
  const isWorking = isStarting || status === 'running';
  const parsedTolerance = Number(toleranceInput);
  // The completed run already covers this exact route + tolerance — no point
  // re-running it. Changing the candidate route or the tolerance re-enables it.
  const alreadyAnalyzed =
    status === 'complete' &&
    !!run &&
    !!selectedRoute &&
    run.candidate.routeId === selectedRoute.id &&
    Number.isFinite(parsedTolerance) &&
    Math.abs(run.config.toleranceMeters - parsedTolerance) < 1e-6;
  const canRun =
    !!feedId && !!selectedRoute && candidateGeometry.length >= 2 && !isWorking && !alreadyAnalyzed;

  return (
    <Dialog
      open={isModalOpen}
      onClose={closeModal}
      maxWidth="md"
      fullWidth
      // The app TopBar (height 72, zIndex 2300) sits above MUI's default dialog
      // (zIndex 1300), so a vertically-centred dialog gets its title clipped behind
      // the bar. Anchor the dialog just below the bar and cap its height to the
      // remaining viewport instead of centring it.
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
      PaperProps={{ sx: { mt: '84px', mb: '24px', maxHeight: 'calc(100vh - 108px)' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography variant="h6">Fixed Route overlap analysis</Typography>
          <Typography variant="body2" color="text.secondary">
            Compare one drawer route against the currently selected hosted network.
          </Typography>
        </Box>
        {status !== 'idle' && (
          <Chip
            size="small"
            color={status === 'complete' ? 'success' : status === 'failed' ? 'error' : status === 'cancelled' ? 'default' : 'info'}
            label={status}
          />
        )}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField
            select
            fullWidth
            label="Candidate route"
            value={selectedCandidateRouteId ?? ''}
            onChange={(e) => setSelectedCandidateRouteId(e.target.value || null)}
          >
            {routes.map((route) => (
              <MenuItem key={route.id} value={route.id}>
                {routeLabel(route)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            label="Tolerance (meters)"
            type="number"
            value={toleranceInput}
            onChange={(e) => setToleranceInput(e.target.value)}
            inputProps={{ min: 1, step: 1 }}
          />
        </Box>

        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip
            label={feedId ? `Feed: ${feedId}` : 'No hosted network selected'}
            color={feedId ? 'primary' : 'default'}
            variant={feedId ? 'filled' : 'outlined'}
          />
          <Chip
            label={`Version: ${scenarioId ?? version ?? 'Base'}`}
            variant="outlined"
          />
          {selectedRoute && (
            <Chip
              label={`Geometry source: ${selectedRoute.geometry?.coordinates?.length ? 'stored route geometry' : 'ordered stop chain'}`}
              variant="outlined"
            />
          )}
        </Box>

        {!feedId && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Pick a hosted network before running overlap analysis.
            <Button size="small" sx={{ ml: 1 }} onClick={() => setPickerOpen(true)}>
              Open Transit Networks
            </Button>
          </Alert>
        )}

        {selectedRoute && candidateGeometry.length < 2 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            This route needs at least two geometry points to compare against the hosted network.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={clearError}>
            {error}
          </Alert>
        )}

        {run?.error && status === 'failed' && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {run.error}
          </Alert>
        )}

        {status === 'idle' && !run && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Run the analysis to see overlapping hosted routes, overlap percentages, and highlighted road segments.
          </Alert>
        )}

        {!!run && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 0.8fr' }, gap: 2 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Overlap results
                </Typography>
                {run.reportSections.map((section) => (
                  <Alert key={section.id} severity="info" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2">{section.heading}</Typography>
                    <Typography variant="body2">{section.body}</Typography>
                  </Alert>
                ))}
                {run.comparisons.length === 0 ? (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    No hosted routes overlapped this candidate within the configured tolerance.
                  </Alert>
                ) : (
                  <List
                    dense
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      mt: 1,
                      maxHeight: { xs: 220, md: 320 },
                      overflowY: 'auto',
                    }}
                  >
                    {run.comparisons.map((comparison) => {
                      const label = comparison.routeShortName || comparison.routeLongName || comparison.routeId;
                      const selected = selectedComparisonRouteId === comparison.routeId;
                      return (
                        <ListItemButton
                          key={comparison.routeId}
                          selected={selected}
                          onClick={() => focusComparisonRoute(comparison.routeId)}
                          alignItems="flex-start"
                        >
                          <ListItemText
                            primary={label}
                            secondary={`${comparison.sharedLengthKm.toFixed(2)} km shared • ${(comparison.candidateOverlapPct * 100).toFixed(1)}% candidate overlap • ${(comparison.networkOverlapPct * 100).toFixed(1)}% hosted overlap`}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Selected result
                </Typography>
                {selectedComparison ? (
                  <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                    <Typography variant="subtitle2">
                      {selectedComparison.routeShortName || selectedComparison.routeLongName || selectedComparison.routeId}
                    </Typography>
                    {selectedComparison.routeLongName && selectedComparison.routeShortName && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {selectedComparison.routeLongName}
                      </Typography>
                    )}
                    <Typography variant="body2">Shared corridor: {selectedComparison.sharedLengthKm.toFixed(2)} km</Typography>
                    <Typography variant="body2">Candidate overlap: {(selectedComparison.candidateOverlapPct * 100).toFixed(1)}%</Typography>
                    <Typography variant="body2">Hosted overlap: {(selectedComparison.networkOverlapPct * 100).toFixed(1)}%</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Map overlays show the candidate route, matched hosted segments, and the selected full hosted route.
                    </Typography>
                  </Box>
                ) : (
                  <Alert severity="info">Select a result row to focus its hosted route on the map.</Alert>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {status === 'running' && (
          <Button onClick={() => void cancelRun()} color="inherit">
            Cancel run
          </Button>
        )}
        <Button onClick={closeModal}>Close</Button>
        <Button
          variant="contained"
          onClick={() => void handleRun()}
          disabled={!canRun}
          startIcon={isWorking ? <CircularProgress size={16} thickness={5} color="inherit" /> : undefined}
        >
          {isWorking
            ? status === 'running'
              ? 'Analyzing…'
              : 'Starting…'
            : alreadyAnalyzed
              ? 'Analyzed'
              : 'Analyze overlap'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
